import { Stack, Typography } from "@mui/material";
import { ReactElement } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "src/foundations/icons";
import { SenecFunctionalIconButton } from "src/components/button/senec-functional-button/SenecFunctionalIconButton";

export interface SenecSectionSwitcherProps {
    /**
     * The text to display in the center of the switcher component. Represents current section.
     */
    text: string;

    /**
     * Callback function that is called when the "Previous" button is clicked.
     */
    onPrevious(): void;

    /**
     * Callback function that is called when the "Next" button is clicked.
     */
    onNext(): void;

    /**
     * Optional flag to disable the "Next" button.
     * Defaults to false.
     */
    isNextDisabled?: boolean;

    /**
     * Optional flag to disable the "Previous" button.
     * Defaults to false.
     */
    isPreviousDisabled?: boolean;

    /**
     * Optional aria-label attribute for the "Previous" button for accessibility.
     * Defaults to 'Previous'.
     */
    previousAriaLabel?: string;

    /**
     * Optional aria-label attribute for the "Next" button for accessibility.
     * Defaults to 'Next'.
     */
    nextAriaLabel?: string;

    /**
     * Optional flag to make the previous and next button invisible.
     */
    showPreviousNextButtons?: boolean;
}

export const SenecSectionSwitcher = ({
    text,
    onPrevious,
    onNext,
    isNextDisabled,
    isPreviousDisabled,
    previousAriaLabel,
    nextAriaLabel,
    showPreviousNextButtons = true,
}: SenecSectionSwitcherProps): ReactElement => {
    return (
        <Stack
            data-testid="senec-section-switcher"
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={3}
            marginY={2}
            gridTemplateColumns={"24px max-content 24px"}
            height={"32px"}
        >
            <>
                {showPreviousNextButtons && (
                    <SenecFunctionalIconButton
                        level="secondary"
                        size="small"
                        disabled={isPreviousDisabled}
                        onClick={onPrevious}
                        aria-label={previousAriaLabel ?? "Previous"}
                    >
                        <ChevronLeftIcon />
                    </SenecFunctionalIconButton>
                )}
            </>
            <Typography variant={"h4"} sx={{ fontSize: "16px !important" }}>
                {text}
            </Typography>
            <>
                {showPreviousNextButtons && (
                    <SenecFunctionalIconButton
                        level="secondary"
                        size="small"
                        disabled={isNextDisabled}
                        onClick={onNext}
                        aria-label={nextAriaLabel ?? "Next"}
                    >
                        <ChevronRightIcon />
                    </SenecFunctionalIconButton>
                )}
            </>
        </Stack>
    );
};
